import type { CSSProperties } from "react";

import { mantineColorRamps, tokens } from "~/ui";

type InboxStyleVarName =
  | "--inbox-white"
  | "--inbox-divider"
  | "--inbox-border"
  | "--inbox-secondary"
  | "--inbox-unread-bg"
  | "--inbox-unread-hover-bg"
  | "--inbox-read-hover-bg"
  | "--inbox-chip-on-bg"
  | "--inbox-chip-on-border"
  | "--inbox-chip-on-text"
  | "--inbox-chip-off-text"
  | "--inbox-link"
  | "--inbox-link-hover-bg"
  | "--inbox-hover-bg"
  | "--inbox-focus-ring"
  | "--inbox-primary"
  | "--inbox-danger"
  | "--inbox-danger-bg"
  | "--inbox-danger-border"
  | "--inbox-shadow-xs"
  | "--inbox-shadow-md";

/**
 * The Inbox's colours as CSS custom properties (SC-3700), set inline on the panel root and on the
 * detail modal's content (which is portalled out of the panel). `InboxPanel.module.css` reads them for
 * the states a prop cannot express (hover, focus-visible), so every colour still comes from `tokens` /
 * `mantineColorRamps` and no hex literal lives in the stylesheet. Same shape as
 * `CalendarPressureBullet.tsx`'s `bulletStyle`. `TaskCard.module.css`'s `withChrome={false}` rows live
 * inside the panel and read the same variables.
 */
export const inboxStyleVars: CSSProperties & Record<InboxStyleVarName, string> = {
  "--inbox-white": tokens.color.white,
  "--inbox-divider": tokens.color.surface.raised,
  "--inbox-border": tokens.color.border.subtle,
  "--inbox-secondary": tokens.color.content.secondary,
  "--inbox-unread-bg": mantineColorRamps.blue[0],
  "--inbox-unread-hover-bg": mantineColorRamps.blue[1],
  "--inbox-read-hover-bg": tokens.color.surface.canvas,
  "--inbox-chip-on-bg": mantineColorRamps.blue[1],
  "--inbox-chip-on-border": mantineColorRamps.blue[3],
  "--inbox-chip-on-text": mantineColorRamps.blue[7],
  "--inbox-chip-off-text": tokens.color.content.primary,
  "--inbox-link": tokens.color.action.informational,
  "--inbox-link-hover-bg": mantineColorRamps.blue[0],
  "--inbox-hover-bg": tokens.color.surface.default,
  "--inbox-focus-ring": tokens.color.action.informational,
  "--inbox-primary": tokens.color.action.primary,
  "--inbox-danger": mantineColorRamps.danger[6],
  "--inbox-danger-bg": mantineColorRamps.danger[0],
  "--inbox-danger-border": tokens.color.border.danger,
  "--inbox-shadow-xs": tokens.shadow.xs,
  "--inbox-shadow-md": tokens.shadow.md,
};
